import { useContext, useMemo } from 'react'
import AnalyticsContext from './AnalyticsContext'
import { useAuth } from '@praxis/component-auth'
import Analytics from './Analytics'
import { EventManagerConfiguration } from './__internals__/EventManager'
import useValueMemo from './__internals__/useValueMemo'

/** Hook providing access to tracking functions for functional components.
 *
 * @param eventManagerConfiguration [Optional]
 * @example
 * const MyComponent = () => {
 *   const {
 *     trackClick,
 *     trackView,
 *     trackCustomEvent,
 *     trackEvent,
 *     trackTaskInit,
 *     trackTaskDone,
 *   } = useAnalytics({ apiKey: '', appName: 'my-app' })
 *
 *   ...
 * }
 */
const useAnalytics = (
  eventManagerConfiguration?: EventManagerConfiguration,
) => {
  const providedAnalytics = useContext(AnalyticsContext)

  const auth = useAuth()

  let loginId: string | undefined
  if (!auth || auth.noProvider === true) {
    // Don't fetch the loginId - we're not in an <AuthProvider>
  } else {
    loginId = auth.session?.userInfo?.lanId
  }

  const memoizedEventManagerConfiguration = useValueMemo(
    eventManagerConfiguration,
  )

  const analytics: Analytics = useMemo(() => {
    if (providedAnalytics) {
      providedAnalytics.loginId = loginId
      return providedAnalytics
    }
    if (memoizedEventManagerConfiguration === undefined) {
      throw new Error(
        `'useAnalytics' must be called from inside an '<AnalyticsProvider>', or must be provided with an 'eventManagerConfiguration' argument.`,
      )
    }
    return new Analytics(memoizedEventManagerConfiguration, loginId)
  }, [memoizedEventManagerConfiguration, loginId, providedAnalytics])

  return analytics
}

export default useAnalytics
